/**
 * Parse rules that let real-world email HTML land in our schema.
 *
 * Every block ships `parseHTML() -> div[data-block-type="…"]`, which only ever
 * matches markup the composer itself wrote. A template authored elsewhere is
 * plain `<table>` / `<td>` / `<h1>` / `<p style>` / `<img>`, so ProseMirror had
 * no rule to match and dropped it — the whole design arrived as bare text.
 * These helpers map that markup onto the blocks and attrs we already have.
 *
 * Two readings of an element's inline styles live here, and they are for
 * different jobs. `parseStyleAttr` splits the raw `style` *attribute string*,
 * so what a block stores is what the designer wrote — including the `mso-*`
 * declarations and unrecognised properties the CSSOM discards outright.
 * `element.style` is still used where a block needs one *resolved* value out of
 * a shorthand — a divider's rule colour out of `border: none; border-top: 2px
 * solid #ccc` — which is parsing the CSSOM already did for us.
 */

const ALIGNMENTS = new Set(["left", "center", "right"]);

/**
 * The alignment an element asks for, before it is narrowed to anything.
 *
 * `""` means the element did not ask, which is the case a block fills in with
 * its own default. Any other value it does not model is a value it cannot
 * reproduce — see `modelsAlignment`.
 */
function declaredAlignment(element: HTMLElement): string {
  return (
    element.style.textAlign ||
    element.getAttribute("align") ||
    ""
  ).toLowerCase();
}

/**
 * Narrow a CSS text-align (or an HTML `align` attribute) to what we support.
 *
 * An element that declares nothing gets nothing back. Defaulting to `left` here
 * looks harmless and is not: the block then renders `text-align:left`, which is
 * an instruction the source never wrote, and it *overrides* the alignment the
 * element was inheriting. A designed email centres a hero by putting
 * `align="center"` on the cell and leaving the heading inside it alone — so
 * inventing `left` for that heading is precisely what pushes it back to the
 * edge, in the composer and in the inbox alike.
 */
export function parseTextAlign(
  element: HTMLElement,
): "" | "left" | "center" | "right" {
  const value = declaredAlignment(element);
  return ALIGNMENTS.has(value) ? (value as "left" | "center" | "right") : "";
}

/**
 * Whether a block's three alignments cover what this element asked for.
 *
 * `text-align: justify` is a real instruction from a designer, and narrowing it
 * to `left` is the composer deciding it knows better. Declining instead sends
 * the element to the fallback tier, which re-emits it exactly as written.
 */
export function modelsAlignment(element: HTMLElement): boolean {
  const value = declaredAlignment(element);
  return !value || ALIGNMENTS.has(value);
}

/**
 * Split a `style` attribute into declarations.
 *
 * A naive `split(";")` cuts a value in half: `background-image:
 * url(data:image/png;base64,\u2026)` carries both a semicolon and a colon inside
 * its parentheses, and a font stack carries them inside quotes. Track both and
 * only break at the top level.
 */
function splitDeclarations(style: string): string[] {
  const declarations: string[] = [];
  let start = 0;
  let depth = 0;
  let quote: string | null = null;

  for (let i = 0; i < style.length; i++) {
    const char = style[i];
    if (quote) {
      if (char === "\\") i++;
      else if (char === quote) quote = null;
    } else if (char === '"' || char === "'") {
      quote = char;
    } else if (char === "(") {
      depth++;
    } else if (char === ")") {
      if (depth > 0) depth--;
    } else if (char === ";" && depth === 0) {
      declarations.push(style.slice(start, i));
      start = i + 1;
    }
  }
  declarations.push(style.slice(start));
  return declarations;
}

/** Every declaration in a `style` attribute string, values kept verbatim. */
export function parseStyleString(style: string): Record<string, string> {
  const parsed: Record<string, string> = {};
  for (const declaration of splitDeclarations(style)) {
    const colon = declaration.indexOf(":");
    if (colon === -1) continue;
    const property = declaration.slice(0, colon).trim();
    const value = declaration.slice(colon + 1).trim();
    if (!property || !value) continue;
    // CSS property names are case-insensitive; custom properties are not.
    parsed[property.startsWith("--") ? property : property.toLowerCase()] =
      value;
  }
  return parsed;
}

/**
 * An element's inline styles, as written.
 *
 * This is the store a block keeps. Nothing is normalised and nothing is
 * dropped, so the schema stops being the ceiling on how much of a design can
 * survive being loaded into the composer.
 */
export function parseStyleAttr(element: HTMLElement): Record<string, string> {
  return parseStyleString(element.getAttribute("style") ?? "");
}

/**
 * The style attrs `emailParagraph` and `emailHeading` share: the alignment they
 * model as their own attr, and everything else exactly as the markup wrote it.
 */
export function parseTextBlockStyleAttrs(element: HTMLElement) {
  return {
    textAlign: parseTextAlign(element),
    style: parseStyleAttr(element),
  };
}

/** The widths the image block's own control offers. */
const IMAGE_WIDTHS = new Set(["25%", "50%", "75%", "100%"]);

/**
 * The image block's own width setting, for an image parsed from markup.
 *
 * It used to nearest-match: a 33% image became 25%, and any pixel width became
 * `auto`. Both were the composer overruling the design. Now anything the
 * control cannot express is simply `auto` here, and the real width is kept in
 * the image's style store — see `parseImageStyle`, which is what actually
 * renders.
 */
export function parseImageWidth(
  element: HTMLElement,
): "auto" | "25%" | "50%" | "75%" | "100%" {
  const declared = (element.style.width || "").trim();
  return IMAGE_WIDTHS.has(declared)
    ? (declared as "25%" | "50%" | "75%" | "100%")
    : "auto";
}

/**
 * An image's styles, with the sizing it was given however it was given.
 *
 * `<img width="120">` is the old spelling and still the common one; folding it
 * into the style store is what lets the image come back out at 120px instead of
 * at whatever the block's default happens to be.
 */
export function parseImageStyle(element: HTMLElement): Record<string, string> {
  const style = parseStyleAttr(element);
  for (const dimension of ["width", "height"] as const) {
    if (style[dimension]) continue;
    const declared = element.getAttribute(dimension)?.trim();
    if (!declared) continue;
    style[dimension] = /^\d+$/.test(declared) ? `${declared}px` : declared;
  }
  return style;
}

/** True when a colour is absent or fully transparent. */
export function isBlankColor(color: string): boolean {
  const value = color.trim().toLowerCase();
  return (
    !value ||
    value === "transparent" ||
    value === "rgba(0, 0, 0, 0)" ||
    value === "none"
  );
}

/** Direct text content only — text inside descendants doesn't count. */
function hasNoDirectText(element: HTMLElement): boolean {
  return Array.from(element.childNodes).every(
    (node) => node.nodeType !== 3 || !(node.textContent ?? "").trim(),
  );
}

function elementChildren(element: HTMLElement): HTMLElement[] {
  return Array.from(element.children) as HTMLElement[];
}

function rowCells(row: HTMLElement): HTMLElement[] {
  return elementChildren(row).filter(
    (child) => child.tagName === "TD" || child.tagName === "TH",
  );
}

/**
 * A cell that exists only to position another table.
 *
 * Email templates nest tables for client compatibility, not for meaning: the
 * canonical shape is a full-width wrapper holding a fixed-width container
 * holding the rows that carry the actual design. Our schema is one level deep
 * (`row > cell > block`), so honouring those wrappers as rows leaves the real
 * rows nowhere to live — they get flattened into a single cell and every band
 * and column in the design is lost with them.
 *
 * Declining the wrapper instead lets ProseMirror walk straight through to the
 * table inside, so the rows that matter become the rows we keep.
 *
 * This is one of the heuristics that survives the fallback tier. Everywhere
 * else, declining now means the element is preserved as written — but a `<tr>`
 * or `<td>` re-emitted outside a table is not markup, so table structure is the
 * one thing the tier cannot take over, and walking through is still the answer.
 */
export function isTableWrapperCell(element: HTMLElement): boolean {
  // A painted cell is either the page gutter or a section's inset, and the
  // difference is depth, not shape. The gutter is the outermost cell of all —
  // preserving it would nest every section inside one row and leave the email
  // uneditable, and the send scaffold supplies the page margin anyway. A cell
  // sitting inside another cell is part of the design: the 48px that holds a
  // callout in from the edge of the card. Flattening that is what makes a
  // section run the full width of the email.
  if (
    carriesDesign(element) &&
    (isNestedInAnotherCell(element) || wrapsSingleRowTable(element))
  ) {
    return false;
  }
  const children = elementChildren(element);
  return (
    children.length > 0 &&
    children.every(
      (child) =>
        child.tagName === "TABLE" && isStackTable(child as HTMLElement),
    ) &&
    hasNoDirectText(element)
  );
}

/**
 * A table that stacks rather than columns: every row holds a single cell.
 *
 * This is the line between scaffolding and design. A page wrapper and a
 * fixed-width container are stacks — they exist to centre and constrain, and
 * flattening them loses nothing. A table with a multi-cell row is a real
 * side-by-side layout, and flattening it would drop a column, so it is kept and
 * nested instead.
 */
/**
 * Does this element carry design of its own, or is it only holding something?
 *
 * The scaffolding tests below rest on a premise — that a wrapper and a
 * fixed-width container "exist to centre and constrain, so flattening them
 * loses nothing". That is true of a bare wrapper and false the moment one is
 * painted. A real designed email puts the white card on the container table,
 * the horizontal inset on the wrapper cell, and a hairline border on the little
 * table around a callout. Flatten those and the card, the inset and the box all
 * go, while every one of their children survives — which reads as a design that
 * fell apart rather than one that was dropped.
 *
 * A `class` counts: the stylesheet it selects is not visible from here, so an
 * element that names one has to be assumed to be styled.
 */
const DESIGN_PROPERTIES = [
  "background",
  "background-color",
  "background-image",
  "border",
  "border-top",
  "border-right",
  "border-bottom",
  "border-left",
  "border-radius",
  "padding",
  "padding-top",
  "padding-right",
  "padding-bottom",
  "padding-left",
];

function carriesDesign(element: HTMLElement): boolean {
  if (element.getAttribute("class")) return true;
  if (element.getAttribute("bgcolor")) return true;
  const declared = parseStyleAttr(element);
  return DESIGN_PROPERTIES.some((property) => {
    const value = declared[property];
    return !!value && !isBlankColor(value);
  });
}

function isStackTable(table: HTMLElement): boolean {
  const rows = Array.from(
    table.querySelectorAll(":scope > tr, :scope > * > tr"),
  );
  return rows.every((row) => rowCells(row as HTMLElement).length <= 1);
}

/**
 * The styles a row should carry, including its table's when they are the same
 * box.
 *
 * A designed email draws a callout as a one-row table with a border on the
 * `<table>` — the `<tr>` inside it is bare. The grid has no node for a table, so
 * reading only the `tr` dropped the border and left the callout as loose text.
 * When the table holds exactly one row, the table's box *is* the row's box, and
 * folding one onto the other loses nothing. With more than one row it would
 * repeat the border on each of them, so it is left alone.
 *
 * The row's own declarations win, being the more specific of the two.
 */
export function parseRowStyleAttr(
  element: HTMLElement,
): Record<string, string> {
  const own = parseStyleAttr(element);
  const table = element.closest("table") as HTMLElement | null;
  if (!table) return own;

  const rows = Array.from(
    table.querySelectorAll(":scope > tr, :scope > * > tr"),
  );
  if (rows.length === 1 && rows[0] === element) {
    return { ...parseStyleAttr(table), ...own };
  }

  // A container's *width* is safe to share across the rows it stacks, which a
  // border or a padding is not: every row is the same width, so repeating it
  // reproduces the container rather than drawing it several times over.
  // Without this the card's 600px is simply lost — every section is as wide as
  // the editor, and an image correctly capped at 600px looks small inside one.
  return { ...containerWidth(table), ...own };
}

/** The width a stacking container hands down, and the centring with it. */
function containerWidth(table: HTMLElement): Record<string, string> {
  if (isPageWrapperTable(table)) return {};
  const declared = parseStyleAttr(table);
  const width = declared.width || declared["max-width"];
  // A percentage is not a card, it is "fill whatever you are in" — which is
  // what a row does anyway.
  if (!width || width.trim().endsWith("%")) return {};
  // A cap, never an outright width. The send scaffold already gives the row a
  // container with side padding, and a row stated at the container's full width
  // would spill out of it. `max-width` leaves the row filling whatever it is
  // in — 570px inside the scaffold, the card's 600px in a wide editor.
  return { "max-width": width, margin: "0 auto" };
}

/** Every table this cell holds has exactly one row. */
function wrapsSingleRowTable(element: HTMLElement): boolean {
  const tables = elementChildren(element).filter(
    (child) => child.tagName === "TABLE",
  );
  if (tables.length === 0) return false;
  return tables.every(
    (table) =>
      table.querySelectorAll(":scope > tr, :scope > * > tr").length === 1,
  );
}

/** Is this cell inside another cell — i.e. part of the design, not the page? */
function isNestedInAnotherCell(element: HTMLElement): boolean {
  return !!element.parentElement?.closest("td, th");
}

/** A row whose single cell is only there to hold a nested table. */
export function isTableWrapperRow(element: HTMLElement): boolean {
  const cells = rowCells(element);
  return cells.length === 1 && isTableWrapperCell(cells[0]);
}

/**
 * The "bulletproof button": a filled `<td>` wrapping a bare `<a>`. The colour
 * lives on the cell because Outlook won't paint a background on a link, so the
 * cell and the link together are one button — not a coloured row that happens
 * to contain text.
 */
export function isButtonCell(element: HTMLElement): boolean {
  const children = elementChildren(element);
  return (
    children.length === 1 &&
    children[0].tagName === "A" &&
    hasNoDirectText(element) &&
    !isBlankColor(parseCellBackground(element))
  );
}

/** A row that is only the frame around a bulletproof button. */
export function isButtonRow(element: HTMLElement): boolean {
  const cells = rowCells(element);
  return cells.length === 1 && isButtonCell(cells[0]);
}

/**
 * The `<td>` that exists to give a link a button's appearance, if there is one.
 *
 * The cell and the link together are one button, and the design is spread
 * across both: Outlook will not paint a background on an `<a>`, so the fill —
 * and, just as often, the rounding and the padding — live on the cell.
 */
function parseButtonFrame(element: HTMLElement): HTMLElement | null {
  const parent = element.parentElement as HTMLElement | null;
  return parent && isButtonCell(parent) ? parent : null;
}

/**
 * The fill a link should use as a button: its own, or the one painted on the
 * `<td>` that exists to give it one.
 */
export function parseButtonBackground(element: HTMLElement): string {
  if (!isBlankColor(element.style.backgroundColor)) {
    return element.style.backgroundColor;
  }
  const frame = parseButtonFrame(element);
  return frame ? parseCellBackground(frame) : "";
}

/**
 * A button's styles, gathered from wherever the design put them.
 *
 * The frame's declarations go underneath the link's own, so a `<td>` carrying
 * `border-radius: 6px` around an unrounded `<a>` produces a rounded button —
 * which is the button the designer drew.
 */
export function parseButtonStyle(element: HTMLElement): Record<string, string> {
  const frame = parseButtonFrame(element);
  return {
    ...(frame ? parseStyleAttr(frame) : {}),
    ...parseStyleAttr(element),
  };
}

/** One of the button's own settings, from the link or from its frame. */
export function parseButtonProperty(
  element: HTMLElement,
  property: "borderRadius" | "borderWidth" | "borderStyle" | "borderColor",
): string {
  if (element.style[property]) return element.style[property];
  const frame = parseButtonFrame(element);
  return frame ? frame.style[property] : "";
}

/**
 * A block that contains nothing but a button-styled link — `<p><a …></a></p>`
 * and friends.
 *
 * A button is a block node, so it cannot live inside a paragraph's inline
 * content: if the paragraph claims the element first, the call to action stays
 * underlined text forever. Declining the paragraph lets the button rule take
 * the link instead. A paragraph with a styled link *among other text* is a
 * different thing and is left alone.
 */
export function wrapsOnlyStyledLink(element: HTMLElement): boolean {
  const children = elementChildren(element);
  return (
    children.length === 1 &&
    children[0].tagName === "A" &&
    hasNoDirectText(element) &&
    !isBlankColor(parseButtonBackground(children[0]))
  );
}

/**
 * A table that exists only to centre another one.
 *
 * Every row it has holds a single cell holding nothing but tables, which is how
 * designed email spells the page backdrop. Its colour belongs to the page, not
 * to the content, so nothing inside it should inherit it.
 */
function isPageWrapperTable(table: HTMLElement): boolean {
  const rows = Array.from(
    table.querySelectorAll(":scope > tr, :scope > * > tr"),
  );
  return (
    rows.length > 0 &&
    rows.every((row) => isTableWrapperRow(row as HTMLElement))
  );
}

/**
 * The background a row should paint.
 *
 * Designed emails put the colour band on the layout `<table>` (or on `<tbody>`)
 * far more often than on the `<tr>` itself, so a rule that only read the `tr`
 * would drop exactly the band the design is built around. Walk outward until a
 * background turns up, stopping at the first table so a nested layout can't
 * inherit its parent's colour.
 */
/**
 * The colour out of a `background` shorthand.
 *
 * Designed email writes `background:#0b5fff` as often as `background-color`,
 * and reading the CSSOM used to resolve the shorthand for us — at the cost of
 * rewriting the value. Splitting it here keeps both: the shorthand is honoured
 * and the colour is still the one that was typed.
 *
 * A bare word is only trusted when it is the whole value; in a multi-part
 * shorthand `center` and `no-repeat` sit where a colour could, so only an
 * unambiguous colour form counts.
 */
const COLOR_VALUE = /^(?:#[0-9a-f]{3,8}|rgba?\([^)]*\)|hsla?\([^)]*\))$/i;

function backgroundShorthandColor(value: string): string {
  const shorthand = value.trim();
  if (!shorthand) return "";
  const tokens = shorthand.match(/(?:[^\s(]+(?:\([^)]*\))?)+/g) ?? [];
  if (tokens.length === 1) {
    return /^[a-z]+$/i.test(tokens[0]) || COLOR_VALUE.test(tokens[0])
      ? tokens[0]
      : "";
  }
  return tokens.find((token) => COLOR_VALUE.test(token)) ?? "";
}

/**
 * A background as the markup declared it.
 *
 * Read from the `style` *attribute* rather than `element.style`, which is a
 * parsed CSSOM and hands back `rgb(26, 26, 26)` for the `#1A1A1A` a designer
 * wrote. That rewrite reaches the inbox, and it is the loss this whole path
 * exists to prevent.
 */
function declaredBackground(element: HTMLElement | null): string {
  if (!element) return "";
  // `parseStyleString` keys are the CSS property names as written, hyphenated.
  const declared = parseStyleAttr(element);
  return (
    declared["background-color"] ||
    backgroundShorthandColor(declared.background ?? "") ||
    element.getAttribute("bgcolor") ||
    ""
  );
}

export function parseRowBackground(element: HTMLElement): string {
  const own = declaredBackground(element);
  if (!isBlankColor(own)) return own;

  const body = element.closest("tbody, thead, tfoot") as HTMLElement | null;
  const bodyBackground = declaredBackground(body);
  if (!isBlankColor(bodyBackground)) return bodyBackground;

  // Walk outward through tables until one is painted. Stopping at the first
  // would mean a callout with no colour of its own hides the card behind it,
  // and the page background shows through a section that should be white.
  //
  // Walk outward to whatever actually shows through this row, stopping at the
  // first ancestor that paints anything — which is what a browser does.
  //
  // Cells count. A chip drawn inside a black header sits on the header's cell,
  // and skipping past it to the white card two levels out paints a white band
  // behind white text: the chip goes invisible in the composer and in the
  // inbox. When the nearest painted ancestor is a cell, that cell renders its
  // own colour and the row must add nothing.
  //
  // A table holding nothing but another table is the page backdrop, and the
  // send scaffold paints the page. Inheriting it would put the backdrop behind
  // every row and leave the design with no edge at all.
  let ancestor = element.parentElement;
  while (ancestor) {
    const tag = ancestor.tagName.toLowerCase();
    if (tag === "td" || tag === "th") {
      if (!isBlankColor(declaredBackground(ancestor))) return "";
    } else if (tag === "table" && !isPageWrapperTable(ancestor)) {
      const background = declaredBackground(ancestor);
      if (!isBlankColor(background)) return background;
    }
    ancestor = ancestor.parentElement;
  }
  return "";
}

/** A cell's own background — the other place a band commonly lives. */
export function parseCellBackground(element: HTMLElement): string {
  const background = declaredBackground(element);
  return isBlankColor(background) ? "" : background;
}

const GRID_COLUMNS = 4;

/**
 * Fit a table cell onto the 4-column grid.
 *
 * This is the cell's span in the *editor*, where columns are resized by
 * dragging on a four-column grid and nothing finer can be expressed. It is a
 * rounding, and for a three- or five-column table it is a lossy one — which is
 * why it no longer decides the rendered width. See `parseCellWidth`.
 */
export function parseCellColSpan(element: HTMLElement): number {
  const siblings = element.parentElement?.children.length ?? 1;
  const declared = Number(element.getAttribute("colspan")) || 1;
  const totalColumns = Math.max(1, siblings);
  const share = (declared / totalColumns) * GRID_COLUMNS;
  return Math.min(GRID_COLUMNS, Math.max(1, Math.round(share)));
}

/**
 * The width a table cell actually occupies.
 *
 * Rendering a cell at `colSpan / 4` is the editor's grid answering a question
 * the markup already answered: three columns came back as 25% each and left a
 * quarter of the row empty, five came back as 125% and overflowed it. The
 * cell's own declared width is the truth where there is one, and its exact
 * share of the row where there is not.
 */
export function parseCellWidth(element: HTMLElement): string {
  const declared = (
    element.style.width ||
    element.getAttribute("width") ||
    ""
  ).trim();
  if (declared) return /^\d+$/.test(declared) ? `${declared}px` : declared;

  const siblings = element.parentElement?.children.length ?? 1;
  if (siblings <= 1) return "";
  const span = Number(element.getAttribute("colspan")) || 1;
  const percent = (span / siblings) * 100;
  // Two decimals: enough that a three-way split adds back up to 100%, and
  // short enough to read in the markup.
  return `${Number(percent.toFixed(2))}%`;
}
